import React from 'react'
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Divider,
    IconButton,
    Stack,
    Typography
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'

import { ProfileData } from '@/src/types/profile'

interface ProfileViewProps {
    profile: ProfileData
    onEdit: () => void
    t: (key: string) => string
    dateOfBirthValue: string
    fullName: string
    initials: string
    phoneLabel: string
    hideTitle?: boolean
}

const DetailItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mt: 0.25 }}>
            {value}
        </Typography>
    </Box>
)

const ProfileView: React.FC<ProfileViewProps> = ({
    profile,
    onEdit,
    t,
    dateOfBirthValue,
    fullName,
    initials,
    phoneLabel,
    hideTitle = false
}) => {

    return (
        <Box sx={{ maxWidth: 980 }}>
            {!hideTitle && (
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        lineHeight: 1.3,
                        color: 'text.primary',
                        mb: { xs: 2, sm: 2.5 }
                    }}
                >
                    {t('title')}
                </Typography>
            )}

            <Card elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ minWidth: 0 }}>
                            <Avatar
                                src={profile.photoFilePath || undefined}
                                sx={{
                                    width: 72,
                                    height: 72,
                                    bgcolor: 'grey.200',
                                    color: 'text.secondary',
                                    fontWeight: 700
                                }}
                            >
                                {initials || <PersonOutlinedIcon sx={{ fontSize: 36 }} />}
                            </Avatar>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        lineHeight: 1.2,
                                        mb: 0.5,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                        {fullName}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        mb: 0.25,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {profile.email || '--'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {phoneLabel}
                                </Typography>
                            </Box>
                        </Stack>

                        <IconButton
                            onClick={onEdit}
                            aria-label={t('editTitle')}
                            sx={{
                                width: 40,
                                height: 40,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 2,
                                flexShrink: 0
                            }}
                        >
                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>

                    <Divider sx={{ my: { xs: 2, sm: 2.5 } }} />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            rowGap: { xs: 2.5, sm: 3 }
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, sm: 4 } }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <DetailItem label={t('gender')} value={profile.gender || '--'} />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <DetailItem label={t('screenName')} value={profile.screenName || '--'} />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, sm: 4 } }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <DetailItem label={t('dateOfBirth')} value={dateOfBirthValue} />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <DetailItem label={t('address')} value={profile.addressLabel || '--'} />
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}

export { ProfileView }
export type { ProfileViewProps }

